import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useWording } from "../../hooks/useWording";
import { PROTOCOL_TASK_STATES } from "../../utils/protocolTasks";

import SkeletonBlock from "../common/SkeletonBlock";
import TaskListEmptyStateCard from "./TaskListEmptyStateCard";

function getStateIcon(state) {
  if (state === "completed") {
    return "check_circle";
  }

  if (state === "overdue") {
    return "error";
  }

  if (state === "due") {
    return "schedule";
  }

  return "radio_button_unchecked";
}

function getStateClasses(state) {
  if (state === "completed") {
    return {
      row: "",
      icon: "text-green-600",
      label: "text-typography",
      meta: "muted-text",
      count: "bg-green-100 text-green-800",
      bareCount: "text-green-700",
    };
  }

  if (state === "overdue") {
    return {
      row: "bg-red-50 text-red-950",
      icon: "text-red-600",
      label: "text-red-900",
      meta: "text-red-700",
      count: "bg-red-100 text-red-800",
      bareCount: "text-red-700",
    };
  }

  if (state === "due") {
    return {
      row: "",
      icon: "text-amber-600",
      label: "text-typography",
      meta: "muted-text",
      count: "bg-amber-100 text-amber-800",
      bareCount: "text-amber-700",
    };
  }

  return {
    row: "",
    icon: "text-greyscale-3",
    label: "text-typography",
    meta: "muted-text",
    count: "bg-greyscale-6 text-typography",
    bareCount: "text-greyscale-3",
  };
}

function TaskListRow({
  task,
  activeTaskId,
  rowClassName = "",
  children,
  bare = false,
  grouped = false,
}) {
  const isInteractive = Boolean(task.href);
  const baseClass = [
    grouped
      ? "w-full bg-card px-3 py-3 text-left shadow"
      : bare
        ? "w-full bg-transparent px-0 py-1.5 text-left"
        : "w-full rounded-2xl bg-container px-3 py-2 text-left",
    "transition-colors duration-200",
    isInteractive ? "group" : "",
    rowClassName,
    activeTaskId === task.id ? "ring-2 ring-primary/30" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (task.href) {
    return (
      <Link
        to={task.href}
        className={baseClass}
        aria-current={activeTaskId === task.id ? "page" : undefined}
        data-testid={`protocol-task-row-${task.id}`}
      >
        {children}
      </Link>
    );
  }

  return (
    <div className={baseClass} data-testid={`protocol-task-row-${task.id}`}>
      {children}
    </div>
  );
}

function getGroupedRowShape(index, total) {
  if (total <= 1) {
    return "rounded-2xl";
  }

  if (index === 0) {
    return "rounded-t-2xl rounded-b-sm";
  }

  if (index === total - 1) {
    return "rounded-t-sm rounded-b-2xl";
  }

  return "rounded-sm";
}

function getTaskMeta(task, t) {
  const meta = [];

  if (task.state && task.state !== "completed") {
    meta.push(t(`taskList.status.${task.state}`));
  }

  return meta;
}

function getOutstandingTaskCount(task) {
  const expectedCount = Number(task?.expectedCount);
  const completedCount = Number(task?.completedCount);

  return Math.max(
    (Number.isFinite(expectedCount) ? expectedCount : 0) -
      (Number.isFinite(completedCount) ? completedCount : 0),
    0,
  );
}

export function ProtocolTaskList({
  board,
  activeTaskId = null,
  titleKey = "taskList.title",
  title = null,
  summary = undefined,
  countDisplay = "remainingPill",
  className = "",
  tasksClassName = "",
  bareRows = false,
  groupedRows = false,
  hideHeader = false,
  emphasizeCounts = false,
  splitCompleted = false,
  outstandingSectionTitle = null,
  completedSectionTitle = null,
}) {
  const { t } = useTranslation("common");
  const { tw } = useWording();
  const sectionClassName = ["w-full card p-2 flex flex-col gap-2", className]
    .filter(Boolean)
    .join(" ");
  const stateSectionClassName = [
    "w-full card p-4 flex flex-col gap-2",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const groupClassName = `flex flex-col ${groupedRows ? "gap-1" : "gap-2"}`;
  const taskRowsClassName = [groupClassName, tasksClassName]
    .filter(Boolean)
    .join(" ");
  const resolvedTitle = title ?? t(titleKey);
  const resolvedSummary =
    summary === undefined
      ? t("taskList.summary", {
          completed: board.summary?.completedTasksCount || 0,
          total: board.summary?.assignedTasksCount || 0,
        })
      : summary;
  const resolvedCompletedSectionTitle =
    completedSectionTitle ?? t("taskList.sections.completed.title");
  if (board?.loading) {
    // The shimmer stands in for the layout that is about to replace it, so a
    // split list loads as its two groups rather than as one block that then
    // rearranges itself.
    const renderSkeletonGroup = (count, groupProps = {}) => (
      <div className={groupClassName} {...groupProps}>
        {Array.from({ length: count }, (_, index) => (
          <SkeletonBlock
            key={index}
            className={`h-16 w-full ${
              groupedRows ? getGroupedRowShape(index, count) : "rounded-2xl"
            }`}
          />
        ))}
      </div>
    );

    return (
      <section className={sectionClassName}>
        {!hideHeader ? <h2 className="subtitle-text">{resolvedTitle}</h2> : null}

        {splitCompleted ? (
          <div
            className={[taskRowsClassName, "!gap-4"].filter(Boolean).join(" ")}
          >
            {outstandingSectionTitle ? (
              <div className={groupClassName}>
                <h3 className="subtitle-text px-1">{outstandingSectionTitle}</h3>
                {renderSkeletonGroup(3, {
                  "data-testid": "protocol-task-group-due",
                })}
              </div>
            ) : (
              renderSkeletonGroup(3, {
                "data-testid": "protocol-task-group-due",
              })
            )}

            <div className={groupClassName}>
              <h3 className="subtitle-text px-1">
                {resolvedCompletedSectionTitle}
              </h3>
              {renderSkeletonGroup(1, {
                "data-testid": "protocol-task-group-completed",
              })}
            </div>
          </div>
        ) : (
          <div className={taskRowsClassName}>
            {Array.from({ length: 3 }, (_, index) => (
              <SkeletonBlock
                key={index}
                className={`h-16 w-full ${
                  groupedRows ? getGroupedRowShape(index, 3) : "rounded-2xl"
                }`}
              />
            ))}
          </div>
        )}
      </section>
    );
  }

  if (board?.state === "error") {
    return (
      <section className={stateSectionClassName}>
        <h2 className="subtitle-text">{t("taskList.unavailable.title")}</h2>
        <p className="muted-text">{t("taskList.unavailable.body")}</p>
      </section>
    );
  }

  if (board?.state === "no-protocol") {
    return (
      <section className={stateSectionClassName}>
        <h2 className="subtitle-text">
          {t("taskList.empty.noProtocol.title")}
        </h2>
        <p className="muted-text">{tw("taskList.empty.noProtocol.body")}</p>
      </section>
    );
  }

  if (board?.state === "unassigned") {
    return (
      <section className={stateSectionClassName}>
        <h2 className="subtitle-text">
          {t("taskList.empty.unassigned.title")}
        </h2>
        <p className="muted-text">{t("taskList.empty.unassigned.body")}</p>
      </section>
    );
  }

  if (!Array.isArray(board?.tasks) || board.tasks.length === 0) {
    return null;
  }

  const renderTaskRow = (task, index, total) => {
    const classes = getStateClasses(task.state);
    const taskMeta = getTaskMeta(task, t);
    const outstandingTaskCount = getOutstandingTaskCount(task);
    const expectedTaskCount = Number(task?.expectedCount);
    const completedTaskCount = Number(task?.completedCount);
    const showsInlineRemainingCount = countDisplay === "remainingInline";
    const showsRemainingPill = countDisplay === "remainingPill";
    const remainingLabel =
      outstandingTaskCount > 0
        ? t(
            outstandingTaskCount === 1
              ? "taskList.remaining.one"
              : "taskList.remaining.other",
            { count: outstandingTaskCount },
          )
        : null;
    const remainingActionLabel =
      outstandingTaskCount > 0
        ? t(
            Number.isFinite(completedTaskCount) &&
              completedTaskCount > 0 &&
              Number.isFinite(expectedTaskCount) &&
              expectedTaskCount > 1
              ? "taskList.actions.continue"
              : "taskList.actions.start",
          )
        : null;
    const inlineMetaLabel = showsInlineRemainingCount
      ? task.state === "completed"
        ? t("taskList.status.completed")
        : taskMeta.length > 0 && remainingLabel
          ? t(
              outstandingTaskCount === 1
                ? "taskList.progress.one"
                : "taskList.progress.other",
              {
                status: taskMeta[0],
                count: outstandingTaskCount,
              },
            )
          : taskMeta[0] || remainingLabel
      : null;
    const showsTaskMeta = !groupedRows && taskMeta.length > 0;
    const usesColourOnlyStatus = bareRows || groupedRows;
    const countClassName = showsRemainingPill
      ? [
          "inline-flex items-center justify-center gap-1 rounded-full px-3 py-1 text-sm font-semibold leading-none whitespace-nowrap",
          groupedRows ? "bg-greyscale-1 shadow-sm" : "",
          groupedRows || usesColourOnlyStatus ? classes.bareCount : classes.count,
        ]
          .filter(Boolean)
          .join(" ")
      : groupedRows
        ? emphasizeCounts
          ? [
              "inline-flex min-w-[3.5rem] items-center justify-center rounded-full bg-greyscale-1 px-3 py-1 text-sm font-bold leading-none shadow-sm",
              classes.bareCount,
            ]
              .filter(Boolean)
              .join(" ")
          : ["text-xs font-semibold", classes.bareCount].filter(Boolean).join(" ")
        : usesColourOnlyStatus
          ? `font-semibold ${classes.bareCount}`
          : `font-semibold rounded-full px-3 py-1 ${classes.count}`;
    const remainingCountBadgeClassName =
      remainingActionLabel && outstandingTaskCount > 0
        ? [
            "inline-flex min-w-6 shrink-0 self-stretch aspect-square items-center justify-center rounded-full px-1 text-sm font-semibold leading-none tabular-nums",
            groupedRows ? "bg-greyscale-1 shadow-sm" : "",
            groupedRows || usesColourOnlyStatus
              ? classes.bareCount
              : classes.count,
          ]
            .filter(Boolean)
            .join(" ")
        : "";

    return (
      <TaskListRow
        key={task.id}
        task={task}
        activeTaskId={activeTaskId}
        rowClassName={
          groupedRows
            ? getGroupedRowShape(index, total)
            : bareRows
              ? ""
              : classes.row
        }
        bare={bareRows}
        grouped={groupedRows}
      >
        <div className="flex items-center gap-2">
          <span
            className={`material-symbols-outlined text-[28px] leading-none ${classes.icon}`}
            aria-hidden="true"
          >
            {getStateIcon(task.state)}
          </span>

          <div className="min-w-0 flex-1 flex flex-col">
            <span
              className={[
                "font-semibold truncate",
                groupedRows ? "text-sm" : "",
                classes.label,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {task.labelKey
                ? t(task.labelKey, task.fallbackLabel)
                : task.fallbackLabel}
            </span>
            {showsInlineRemainingCount ? (
              inlineMetaLabel ? (
                <span
                  className={[
                    "inline-flex items-center gap-1 leading-none",
                    groupedRows ? "!text-xs" : "text-sm",
                    classes.meta,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span>{inlineMetaLabel}</span>
                  {task.href ? (
                    <span
                      className="material-symbols-outlined text-[16px] leading-none transition-transform duration-200 group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5"
                      aria-hidden="true"
                    >
                      chevron_right
                    </span>
                  ) : null}
                </span>
              ) : null
            ) : showsTaskMeta ? (
              <span
                className={[groupedRows ? "!text-xs" : "", classes.meta]
                  .filter(Boolean)
                  .join(" ")}
              >
                {taskMeta.join(" · ")}
              </span>
            ) : null}
          </div>

          {!showsInlineRemainingCount ? (
            <div className="shrink-0 flex items-center gap-2">
              {showsRemainingPill ? (
                remainingActionLabel ? (
                    <>
                      <span
                        className={remainingCountBadgeClassName}
                        data-testid={`protocol-task-row-outstanding-count-${task.id}`}
                        aria-label={remainingLabel || undefined}
                        title={remainingLabel || undefined}
                      >
                        {outstandingTaskCount}
                      </span>
                      <span className={countClassName}>
                        <span>{remainingActionLabel}</span>
                        {task.href ? (
                          <span
                            className="material-symbols-outlined text-[16px] leading-none transition-transform duration-200 group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5"
                            aria-hidden="true"
                          >
                            chevron_right
                          </span>
                        ) : null}
                      </span>
                    </>
                ) : null
              ) : (
                <>
                  <span
                    className={[
                      "tabular-nums",
                      groupedRows ? "" : "text-sm",
                      countClassName,
                    ].join(" ")}
                  >
                    {task.completedCount}/{task.expectedCount}
                  </span>

                  {task.href ? (
                    <span
                      className="material-symbols-outlined text-[18px] leading-none text-greyscale-3 transition-transform duration-200 group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5"
                      aria-hidden="true"
                    >
                        chevron_right
                      </span>
                    ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>
      </TaskListRow>
    );
  };

  const renderTaskGroup = (
    tasks,
    {
      emptyTitle = null,
      emptyMessage,
      emptyIcon,
      emptyIconClassName = undefined,
      emptyTitleClassName = undefined,
      emptyTextClassName = undefined,
      testId,
    },
  ) => (
    <div className={groupClassName} data-testid={testId}>
      {tasks.length ? (
        tasks.map((task, index) => renderTaskRow(task, index, tasks.length))
      ) : (
        <TaskListEmptyStateCard
          icon={emptyIcon}
          title={emptyTitle}
          message={emptyMessage}
          grouped={groupedRows}
          iconClassName={emptyIconClassName}
          titleClassName={emptyTitleClassName}
          textClassName={emptyTextClassName}
        />
      )}
    </div>
  );
  const renderTitledTaskGroup = (
    tasks,
    {
      title: groupTitle = null,
      emptyTitle = null,
      emptyMessage,
      emptyIcon,
      emptyIconClassName = undefined,
      emptyTitleClassName = undefined,
      emptyTextClassName = undefined,
      testId,
    },
  ) => {
    const content = renderTaskGroup(tasks, {
      emptyTitle,
      emptyMessage,
      emptyIcon,
      emptyIconClassName,
      emptyTitleClassName,
      emptyTextClassName,
      testId,
    });

    if (!groupTitle) {
      return content;
    }

    return (
      <div className={groupClassName}>
        <h3 className="subtitle-text px-1">{groupTitle}</h3>
        {content}
      </div>
    );
  };

  const header = !hideHeader ? (
    <div className="flex items-center justify-between gap-2 px-1">
      <h2 className="subtitle-text">{resolvedTitle}</h2>
      {resolvedSummary !== null ? (
        <p className="muted-text">{resolvedSummary}</p>
      ) : null}
    </div>
  ) : null;

  if (splitCompleted) {
    const outstandingTasks = board.tasks.filter(
      (task) => task.state !== PROTOCOL_TASK_STATES.COMPLETED,
    );
    const completedTasks = board.tasks.filter(
      (task) => task.state === PROTOCOL_TASK_STATES.COMPLETED,
    );

    return (
      <section className={sectionClassName}>
        {header}

        {/* The outstanding rows keep the whole list's scroll box; the completed
            ones follow underneath as a second, separately shaped stack. */}
        <div
          className={[taskRowsClassName, "!gap-4 overflow-y-auto"]
            .filter(Boolean)
            .join(" ")}
        >
          {outstandingTasks.length
            ? renderTitledTaskGroup(outstandingTasks, {
                title: outstandingSectionTitle,
                testId: "protocol-task-group-due",
              })
            : null}

          {renderTitledTaskGroup(completedTasks, {
            title: resolvedCompletedSectionTitle,
            emptyMessage: t("taskList.sections.completed.empty"),
            emptyIcon: "pending_actions",
            testId: "protocol-task-group-completed",
          })}
        </div>
      </section>
    );
  }

  return (
    <section className={sectionClassName}>
      {header}

      <div className={taskRowsClassName}>
        {board.tasks.map((task, index) =>
          renderTaskRow(task, index, board.tasks.length),
        )}
      </div>
    </section>
  );
}

export default React.memo(ProtocolTaskList);
